import { useCallback } from 'react'

import { useNavigate } from 'react-router-dom'

import { RoutesNames } from '@/routes/routes'
import { BridgeService } from '@/services'
import useGlobalState from '@/contexts/global-state'
import * as Actions from '@/contexts/global-state/reducer/actions'
import { handleNavigation } from '@/components/DynamicComponent/helpers'

const requestOnboardingAnalytics = (analytics: Analytics) => {
  let eventName: string | undefined
  if (typeof analytics.name === 'string') {
    eventName = analytics.name
  } else if (typeof analytics.event === 'string') {
    eventName = analytics.event
  }
  if (eventName === undefined) {
    return
  }

  const params = Object.fromEntries(
    Object.entries(analytics)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)]),
  ) as Record<string, string>

  BridgeService.requestAnalytics(eventName, params).catch(() => {
    /* bridge silenciado: evita unhandled rejection no webview */
  })
}

type SDUIAction =
  | NavigationAction
  | BottomSheetAction
  | { type: 'path'; route: string; skeleton?: Skeleton }

/**
 * Hook centralizado que dispara e executa ações SDUI.
 * Trata: navigation, path e bottom-sheet.
 */
export const useActionHandler = () => {
  const navigate = useNavigate()
  const [, dispatch] = useGlobalState()

  const dispatchAction = useCallback(
    (action: SDUIAction, analytics?: Analytics) => {
      if (analytics && Object.keys(analytics).length > 0) {
        requestOnboardingAnalytics(analytics)
      }
      switch (action.type) {
        case 'navigation': {
          const navAction = action as NavigationAction
          handleNavigation(navAction.route)
          break
        }
        case 'path': {
          const pathAction = action as NavigationAction
          dispatch(Actions.setApiRequest({ apiRequest: pathAction.route }))
          dispatch(Actions.setSkeleton({ skeleton: pathAction.skeleton }))
          navigate({ pathname: RoutesNames.ROOT })
          break
        }
        case 'bottom-sheet': {
          const bottomSheetAction = action as BottomSheetAction
          dispatch(
            Actions.openGlobalBottomSheet({
              title: bottomSheetAction.title,
              expansible: bottomSheetAction.expansible,
              position: bottomSheetAction.position,
              components: bottomSheetAction.components ?? [],
            }),
          )
          break
        }
        default:
          break
      }
    },
    [dispatch, navigate],
  )

  return { dispatchAction }
}
